import type { ResosysTourBooking } from "@getaways/tours";
import { api, Badge, Banner, dateTime, Empty, go, PageHead, stackOnMobile, ToggleGroup, useAsync, useStoredState } from "@getaways/extranet-ui";
import { IconSearch } from "@tabler/icons-react";
import { useState } from "react";
import { amount, localDate } from "../lib/format";
import { BRANDS, lead, STATUS } from "../lib/labels";

const FILTERS = ["", "held", "on_request", "confirmed", "cancellation_requested", "cancelled", "expired", "travelled"] as const;
const PAGE = 50;

export function BookingsPage() {
  const [status, setStatus] = useStoredState<string>("tours.bookings.status", "");
  const [text, setText] = useState("");
  const [q, setQ] = useState("");
  const [offset, setOffset] = useState(0);
  const params = [status ? `status=${status}` : "", q.length >= 2 ? `q=${encodeURIComponent(q)}` : "", `limit=${PAGE}`, `offset=${offset}`].filter(Boolean).join("&");
  const { data, error } = useAsync(() => api<{ bookings: ResosysTourBooking[]; total: number }>("GET", `/v1/admin/bookings?${params}`), [params]);

  return (
    <>
      <PageHead title="Bookings" sub={data ? `${data.total} booking${data.total === 1 ? "" : "s"}` : undefined} />
      {error && <Banner notice={{ tone: "bad", text: error }} />}
      <div className="card" style={{ padding: 0 }}>
        <div className="row" style={{ padding: "12px 14px", borderBottom: "1px solid var(--line)" }}>
          {/* Toggle values can't be empty, so "All" (no status filter) is the value "all" here. */}
          <ToggleGroup
            label="Filter bookings"
            value={status || "all"}
            onValueChange={(v) => { setStatus(v === "all" ? "" : v); setOffset(0); }}
            items={FILTERS.map((f) => ({ value: f || "all", label: f ? STATUS[f][1] : "All" }))}
          />
          <span className="spacer" />
          <form style={{ position: "relative", width: 260 }} onSubmit={(e) => { e.preventDefault(); setQ(text.trim()); setOffset(0); }}>
            <IconSearch size={16} style={{ position: "absolute", left: 10, top: 9, color: "var(--faint)" }} aria-hidden />
            <input value={text} onChange={(e) => setText(e.target.value)} onBlur={() => setQ(text.trim())} placeholder="Reference, tour or name" style={{ paddingLeft: 32 }} aria-label="Search bookings" />
          </form>
        </div>
        {data && data.bookings.length === 0 ? <Empty>No bookings match.</Empty> : (
          <table ref={stackOnMobile} className="table stack-mobile">
            <thead><tr><th>Tour</th><th>Starts</th><th>Lead traveller</th><th>Reference</th><th>Status</th><th className="num">Sold for</th><th>Created</th></tr></thead>
            <tbody>
              {(data?.bookings ?? []).map((b) => (
                <tr key={b.id} className="click" onClick={() => go("bookings", b.id)}>
                  <td>{b.tour.name}<div className="small muted">{b.room.name} · {b.travellers.length} traveller{b.travellers.length === 1 ? "" : "s"} · {BRANDS[b.brand] ?? b.brand}</div></td>
                  <td>{localDate(b.departure.start)}{b.tour.durationDays && <div className="small muted">{b.tour.durationDays} days</div>}</td>
                  <td>{lead(b) ? `${lead(b)!.firstName} ${lead(b)!.lastName}` : "—"}</td>
                  <td><span className="mono">{b.clientReference}</span><div className="small muted">{b.supplier} {b.supplierReference}</div></td>
                  <td><Badge tone={STATUS[b.status][0]}>{STATUS[b.status][1]}</Badge>{b.requirements.length > 0 && <Badge tone="warn">data missing</Badge>}</td>
                  <td className="num">{amount(b.sell)}</td>
                  <td className="small muted">{dateTime(b.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {data && data.total > PAGE && (
          <div className="row" style={{ padding: "10px 14px" }}>
            <span className="small muted">{offset + 1}–{Math.min(offset + PAGE, data.total)} of {data.total}</span>
            <span className="spacer" />
            <button disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE))}>Previous</button>
            <button disabled={offset + PAGE >= data.total} onClick={() => setOffset(offset + PAGE)}>Next</button>
          </div>
        )}
      </div>
    </>
  );
}
